import { OLDER_PAGE_BAR_COUNT, HISTORY_PRELOAD_SCREENS } from "../constants";

const MS_PER_SECOND = 1000;

/**
 * getBars periodParams (seconds) for `barCount` bars that end at `to`.
 *
 * @param {number} to - seconds
 * @param {number} barCount
 * @param {number} barDurationMs
 * @param {boolean} firstDataRequest
 */
export const periodBefore = (to, barCount, barDurationMs, firstDataRequest) => ({
  from: to - (barCount * barDurationMs) / MS_PER_SECOND,
  to,
  countBack: barCount,
  firstDataRequest,
});

/**
 * Older history of one datafeed session: requests a page before the loaded
 * bars when fewer than HISTORY_PRELOAD_SCREENS screens of bars are left of
 * the view. One request at a time; after "no data" nothing more is
 * requested. Each page ends where the previous request started, like in
 * TradingView, so an empty page moves the next one further back.
 *
 * @param {object} params
 * @param {object} params.session    - from openDatafeedSession, already resolved
 * @param {object} params.store      - BarStore that receives the older bars
 * @param {number} params.barDurationMs
 * @param {number} params.firstPageFrom - `from` of the first page, seconds
 * @param {boolean} params.hasMore   - false when the first page said "no data"
 * @param {() => void} params.onPrepended - older bars were added to the store
 * @returns {{ handleVisibleRange: (range: { first: number, last: number }) => void }}
 */
export const createHistoryPager = ({
  session,
  store,
  barDurationMs,
  firstPageFrom,
  hasMore,
  onPrepended,
}) => {
  let nextTo = firstPageFrom;
  let hasOlderBars = hasMore;
  let isLoading = false;
  let lastRange = null;

  const loadOlder = (onEmptyPage) => {
    const period = periodBefore(nextTo, OLDER_PAGE_BAR_COUNT, barDurationMs, false);

    isLoading = true;
    nextTo = period.from;

    session.getBars(period, (bars, noData) => {
      isLoading = false;
      hasOlderBars = !noData;

      if (store.prependBars(bars) > 0) {
        onPrepended();
      } else {
        onEmptyPage();
      }
    });
  };

  const handleVisibleRange = (range) => {
    lastRange = range;

    if (isLoading || !hasOlderBars || !store.length) return;

    const visibleBarCount = Math.max(1, range.last - range.first + 1);

    if (range.first < visibleBarCount * HISTORY_PRELOAD_SCREENS) {
      loadOlder(() => handleVisibleRange(lastRange));
    }
  };

  return { handleVisibleRange };
};
